import React from 'react';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '../../context/ThemeContext';

export interface DangerButtonProps extends Omit<ButtonProps, 'variant'> {
  loading?: boolean;
  outlined?: boolean;
}

const DangerButton: React.FC<DangerButtonProps> = ({
  children,
  loading = false,
  disabled,
  outlined = false,
  sx,
  ...props
}) => {
  const { theme } = useTheme();
  const btn = theme.button.danger;

  const containedSx = {
    backgroundColor: btn.solidBg ?? btn.bg,
    color: btn.solidText ?? btn.text,
    '&:hover': {
      backgroundColor: btn.solidHover ?? btn.hover,
    },
  };

  const outlinedSx = {
    backgroundColor: 'transparent',
    color: btn.text,
    border: `1px solid ${btn.text}`,
    '&:hover': {
      backgroundColor: btn.bg,
      border: `1px solid ${btn.text}`,
    },
  };

  return (
    <Button
      variant={outlined ? 'outlined' : 'contained'}
      disabled={disabled || loading}
      sx={{
        minHeight: 44,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '6px',
        ...(outlined ? outlinedSx : containedSx),
        '&:active': {
          backgroundColor: btn.pressed,
        },
        '&.Mui-disabled': {
          backgroundColor: btn.disabled ?? theme.gray[200],
          color: theme.gray[400],
        },
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" sx={{ mr: children ? 1 : 0 }} />
      ) : null}
      {children}
    </Button>
  );
};

export default DangerButton;
